<template>
  <div class="pd3-page">

    <!-- ── Top bar ── -->
    <div class="pd3-topbar" :class="promo && promo.is_template ? 'pd3-topbar--template' : ''">
      <v-btn small tile outlined :color="promo && promo.is_template ? 'deep-purple' : 'info'" @click="$router.push('/promociones-v3')">
        <v-icon left>mdi-arrow-left-bold</v-icon> Volver
      </v-btn>

      <!-- Ícono + badge PLANTILLA -->
      <v-icon :color="promo && promo.is_template ? 'deep-purple' : 'blue darken-4'" class="ml-3">
        {{ promo && promo.is_template ? 'mdi-bookmark-outline' : 'mdi-tag-outline' }}
      </v-icon>
      <span class="subtitle-1 font-weight-bold ml-1 text-truncate" style="max-width:340px;">
        {{ promo ? promo.nombre : 'Cargando...' }}
      </span>

      <!-- Badge PLANTILLA destacado -->
      <v-chip
        v-if="promo && promo.is_template"
        small color="deep-purple" dark class="ml-2 font-weight-bold"
      >
        <v-icon left x-small>mdi-bookmark-check</v-icon>PLANTILLA
      </v-chip>

      <v-chip v-if="promo && promo.estado !== null && promo.estado !== undefined" x-small :color="estadoColor(promo.estado)" dark class="ml-1 font-weight-bold">
        {{ promo.estado === 0 ? 'No Aprobada' : promo.estado === 1 ? 'Aprobada' : promo.estado }}
      </v-chip>
      <v-spacer />

      <!-- Botón Editar -->
      <v-btn
        v-if="promo"
        small depressed color="orange darken-2" dark class="mr-2"
        @click="$router.push(`/promociones-v3/${promo.id}/editar`)"
      >
        <v-icon left small>mdi-pencil-outline</v-icon>
        Editar
      </v-btn>

      <!-- Botón Aprobar Plan -->
      <v-btn
        v-if="promo && promo.estado === 0"
        small depressed color="green darken-1" dark class="mr-2"
        :loading="aprobando"
        @click="dialogAprobar = true"
      >
        <v-icon left small>mdi-check-circle-outline</v-icon>
        Aprobar Plan
      </v-btn>

      <!-- Si es plantilla: botón "Crear promoción desde esta plantilla" -->
      <v-btn
        v-if="promo && promo.is_template"
        small color="deep-purple" dark depressed
        @click="usarComoPlantilla"
      >
        <v-icon left small>mdi-plus-circle-outline</v-icon>
        Crear promoción desde esta plantilla
      </v-btn>

      <!-- Si es promoción normal: botón "Usar como plantilla" -->
      <v-btn
        v-if="promo && !promo.is_template"
        small outlined color="blue darken-4"
        @click="usarComoPlantilla"
      >
        <v-icon left small>mdi-bookmark-plus-outline</v-icon>
        Usar como plantilla
      </v-btn>
    </div>

    <v-progress-linear v-if="loading" :color="promo && promo.is_template ? 'deep-purple' : 'blue darken-4'" indeterminate height="3" />

    <!-- Banner plantilla -->
    <div v-if="promo && promo.is_template" class="template-banner">
      <v-icon small color="deep-purple darken-1" class="mr-2">mdi-information-outline</v-icon>
      <span>
        Estás viendo una <strong>plantilla reutilizable</strong>. No tiene fechas activas —
        úsala como base para crear una nueva promoción con un clic.
      </span>
    </div>

    <!-- Error -->
    <div v-if="error" class="pd3-body">
      <v-alert type="error" outlined>
        No se pudo cargar la promoción. Verifica que el ID sea válido.
      </v-alert>
    </div>

    <!-- Contenido -->
    <div v-if="promo && !loading" class="pd3-body">

      <!-- ── 1. Cabecera informativa ── -->
      <v-card outlined class="mb-5">
        <v-card-text class="pa-5">
          <v-row dense align="start">

            <!-- Nombre -->
            <v-col cols="12" md="6">
              <div class="detail-label">{{ promo.is_template ? 'NOMBRE DE LA PLANTILLA' : 'NOMBRE DE LA PROMOCIÓN' }}</div>
              <div class="detail-value text-h6 font-weight-bold mt-1" :class="promo.is_template ? 'deep-purple--text text--darken-2' : 'blue--text text--darken-4'">
                {{ promo.nombre }}
              </div>
              <div v-if="promo.descripcion" class="caption grey--text text--darken-1 mt-1">{{ promo.descripcion }}</div>
            </v-col>

            <!-- Columna derecha: chips + meta -->
            <v-col cols="12" md="6">
              <v-row dense>
                <v-col cols="6">
                  <div class="detail-label">ESTADO</div>
                  <v-chip :color="estadoColor(promo.estado)" dark small class="font-weight-bold mt-1">
                    <v-icon left x-small>{{ promo.estado === 1 ? 'mdi-check-circle' : 'mdi-clock-outline' }}</v-icon>
                    {{ promo.estado === 0 ? 'No Aprobada' : promo.estado === 1 ? 'Aprobada' : promo.estado }}
                  </v-chip>
                </v-col>
                <v-col cols="6">
                  <div class="detail-label">UNIDAD DE NEGOCIO</div>
                  <div class="detail-value d-flex align-center mt-1">
                    <v-icon small class="mr-1" color="grey darken-1">mdi-domain</v-icon>
                    {{ unidadNombre || '—' }}
                  </div>
                </v-col>
                <v-col cols="6" class="mt-3">
                  <div class="detail-label">CREADO POR</div>
                  <div class="detail-value d-flex align-center mt-1">
                    <v-icon small class="mr-1" color="grey darken-1">mdi-account-plus-outline</v-icon>
                    {{ promo.creado_por || '—' }}
                  </div>
                  <div v-if="promo.created_at" class="caption grey--text mt-0">{{ promo.created_at }}</div>
                </v-col>
                <v-col cols="6" class="mt-3">
                  <div class="detail-label">EDITADO POR</div>
                  <div class="detail-value d-flex align-center mt-1">
                    <v-icon small class="mr-1" color="blue darken-1">mdi-account-edit-outline</v-icon>
                    {{ promo.actualizado_por || '—' }}
                  </div>
                  <div v-if="promo.updated_at" class="caption grey--text mt-0">{{ promo.updated_at }}</div>
                </v-col>
                <v-col cols="6" class="mt-3" v-if="promo.aprobado_por">
                  <div class="detail-label">APROBADO POR</div>
                  <div class="detail-value d-flex align-center mt-1">
                    <v-icon small class="mr-1" color="green darken-1">mdi-account-check-outline</v-icon>
                    <span class="green--text text--darken-2 font-weight-medium">{{ promo.aprobado_por }}</span>
                  </div>
                </v-col>
                <v-col cols="6" class="mt-3" v-if="promo.deleted_at || promo.eliminado_por">
                  <div class="detail-label">ELIMINADO POR</div>
                  <div class="detail-value d-flex align-center mt-1">
                    <v-icon small class="mr-1" color="red darken-1">mdi-account-remove-outline</v-icon>
                    <span class="red--text text--darken-2 font-weight-medium">{{ promo.eliminado_por || promo.deleted_by || '—' }}</span>
                  </div>
                  <div v-if="promo.deleted_at" class="caption red--text text--lighten-1 mt-0">{{ promo.deleted_at }}</div>
                </v-col>
              </v-row>
            </v-col>

          </v-row>
        </v-card-text>
      </v-card>

      <!-- ── 2. Reglas de Descuento ── -->
      <div class="d-flex align-center mb-3">
        <div class="section-num">2</div>
        <v-icon color="blue darken-4" class="ml-3 mr-2">mdi-percent-outline</v-icon>
        <span class="subtitle-1 font-weight-bold">Reglas de Descuento</span>
        <v-chip x-small color="blue darken-4" dark class="ml-2">{{ promo.reglas.length }}</v-chip>
        <v-spacer />
        <!-- Toggle de vista -->
        <v-btn-toggle v-model="vistaTabla" mandatory dense class="ml-2">
          <v-btn :value="false" x-small outlined>
            <v-icon x-small left>mdi-card-text-outline</v-icon>Tarjetas
          </v-btn>
          <v-btn :value="true" x-small outlined>
            <v-icon x-small left>mdi-table</v-icon>Tabla
          </v-btn>
        </v-btn-toggle>
      </div>

      <!-- ── Vista Tarjetas (original) ── -->
      <template v-if="!vistaTabla">
        <v-card
          v-for="(regla, ri) in promo.reglas"
          :key="ri"
          outlined
          class="mb-4 regla-card"
        >
          <!-- Cabecera de regla -->
          <div class="regla-hdr">
            <v-chip small color="blue darken-4" dark class="mr-3 flex-shrink-0">Regla #{{ ri + 1 }}</v-chip>
            <div class="d-flex align-center flex-wrap" style="gap:4px; flex:1; min-width:0;">
              <v-chip v-if="regla._categoria" x-small outlined color="indigo">
                <v-icon x-small left>mdi-shape-outline</v-icon>{{ regla._categoria }}
              </v-chip>
              <v-chip v-if="regla._area" x-small outlined color="teal">
                <v-icon x-small left>mdi-map-marker-outline</v-icon>{{ regla._area }}
              </v-chip>
              <v-chip v-if="regla.modalidad" x-small outlined color="purple">
                <v-icon x-small left>mdi-school-outline</v-icon>{{ labelModalidad(regla.modalidad) }}
              </v-chip>
              <v-chip v-if="regla.plan_pago" x-small outlined color="deep-orange">
                <v-icon x-small left>mdi-credit-card-outline</v-icon>
                {{ regla.plan_pago === 'credito' ? 'Crédito' : 'Contado' }}
              </v-chip>
              <v-chip v-if="regla.tipo_precio" x-small outlined color="brown">
                <v-icon x-small left>mdi-tag-outline</v-icon>{{ labelTipoPrecio(regla.tipo_precio) }}
              </v-chip>
              <v-chip v-if="regla.estado" x-small :color="estadoColor(regla.estado)" dark>
                {{ regla.estado }}
              </v-chip>
              <v-chip v-if="regla.estado_programa_id" x-small :color="estadoColorPrograma(regla.estado_programa_id)" dark>
                <v-icon x-small left>mdi-state-machine</v-icon>
                {{ labelEstadoPrograma(regla.estado_programa_id) }}
              </v-chip>
            </div>
          </div>

          <!-- Conceptos -->
          <div class="px-4 pb-3 pt-2">
            <div v-if="!regla.conceptos || regla.conceptos.length === 0" class="text-center py-4 grey--text caption">
              Sin conceptos en esta regla
            </div>
            <v-data-table
              v-else
              :headers="conceptosHeaders"
              :items="regla._conceptos"
              hide-default-footer
              disable-pagination
              :items-per-page="-1"
              class="elevation-0 conceptos-tbl"
              dense
            >
              <template v-slot:[`item.is_porcentaje`]="{ item }">
                <v-chip x-small :color="item.is_porcentaje ? 'indigo' : 'teal'" dark>
                  {{ item.is_porcentaje ? '%' : 'Bs' }}
                </v-chip>
              </template>
              <template v-slot:[`item.valor_descuento`]="{ item }">
                <span class="orange--text text--darken-2 font-weight-bold">
                  {{ item.is_porcentaje ? item.valor_descuento + '%' : 'Bs ' + parseFloat(item.valor_descuento).toFixed(2) }}
                </span>
              </template>
              <template v-slot:[`item.precio_final`]="{ item }">
                <span class="green--text text--darken-2 font-weight-bold">
                  Bs {{ pFinal(item).toFixed(2) }}
                </span>
              </template>
              <template v-slot:[`item.valor_cuota`]="{ item }">
                <span class="blue--text text--darken-2 font-weight-bold">
                  Bs {{ vCuota(item).toFixed(2) }}
                </span>
              </template>
            </v-data-table>
          </div>

          <!-- Totales -->
          <v-divider />
          <div class="regla-totals">
            <div class="rtot-cell">
              <div class="rtot-label"><v-icon x-small class="mr-1">mdi-cash-register</v-icon>TOTAL BASE</div>
              <div class="rtot-value">Bs {{ tBase(regla).toFixed(2) }}</div>
            </div>
            <v-divider vertical />
            <div class="rtot-cell rtot-cell--green">
              <div class="rtot-label rtot-label--green"><v-icon x-small class="mr-1">mdi-piggy-bank-outline</v-icon>AHORRO TOTAL</div>
              <div class="rtot-value rtot-value--green">Bs {{ tAhorro(regla).toFixed(2) }}</div>
            </div>
            <v-divider vertical />
            <div class="rtot-cell rtot-cell--blue">
              <div class="rtot-label rtot-label--blue"><v-icon x-small class="mr-1">mdi-numeric-1-circle-outline</v-icon>PAGO INICIAL (1ERA CUOTA)</div>
              <div class="rtot-value rtot-value--blue rtot-value--lg">Bs {{ tPrimeraCuota(regla).toFixed(2) }}</div>
            </div>
            <v-divider vertical />
            <div class="rtot-cell rtot-cell--dark">
              <div class="rtot-label rtot-label--light">
                <v-icon x-small class="mr-1" color="white" style="opacity:.7;">mdi-trophy-outline</v-icon>
                <span style="opacity:.7;">PRECIO FINAL TOTAL</span>
              </div>
              <div class="rtot-value rtot-value--white rtot-value--lg">Bs {{ tFinal(regla).toFixed(2) }}</div>
            </div>
          </div>
        </v-card>
      </template>

      <!-- ── Vista Tabla ── -->
      <div v-else class="xtabla-wrap">
        <template v-if="tablaData">
          <div
            v-for="grupo in tablaData.grupos"
            :key="grupo.nombre"
            class="xtabla-grupo mb-5"
          >
            <!-- Cabecera de categoría -->
            <div class="xtabla-cat-hdr">
              <v-icon small color="white" class="mr-2">mdi-shape-outline</v-icon>
              {{ grupo.nombre || 'Sin categoría' }}
              <v-chip x-small dark color="white" class="ml-2" style="color:#1565c0;">
                {{ grupo.filas.length }} regla{{ grupo.filas.length !== 1 ? 's' : '' }}
              </v-chip>
            </div>

            <!-- Tabla con scroll horizontal -->
            <div class="xtabla-scroll">
              <table class="xtabla">
                <!-- ── Fila 1: grupos de conceptos ── -->
                <thead>
                  <tr>
                    <th class="xt-th xt-th-area" rowspan="2">ÁREA</th>
                    <th class="xt-th xt-th-meta" rowspan="2">MODALIDAD<br>/ PLAN</th>
                    <th
                      v-for="c in tablaData.conceptos" :key="'gh-'+c"
                      colspan="5"
                      class="xt-th xt-th-group"
                    >{{ c }}</th>
                    <th colspan="4" class="xt-th xt-th-group xt-th-group--summary">RESUMEN</th>
                  </tr>
                  <!-- ── Fila 2: sub-columnas ── -->
                  <tr>
                    <th
                      v-for="col in tablaColumnasSub" :key="col.key"
                      :class="['xt-th', 'xt-th-sub', col.cls]"
                    >{{ col.label }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(fila, fi) in grupo.filas"
                    :key="'r'+fi"
                    :class="fi % 2 === 0 ? 'xt-tr-even' : 'xt-tr-odd'"
                  >
                    <td class="xt-td xt-td-area">{{ fila.regla._area || '—' }}</td>
                    <td class="xt-td xt-td-meta">
                      <div v-if="fila.regla.modalidad" class="xt-badge xt-badge--purple">{{ labelModalidad(fila.regla.modalidad) }}</div>
                      <div v-if="fila.regla.plan_pago" class="xt-badge xt-badge--orange">{{ fila.regla.plan_pago === 'credito' ? 'Crédito' : 'Contado' }}</div>
                      <div v-if="fila.regla.tipo_precio" class="xt-badge xt-badge--brown">{{ labelTipoPrecio(fila.regla.tipo_precio) }}</div>
                    </td>
                    <td
                      v-for="celda in getCeldasFila(fila)" :key="celda.key"
                      :class="celda.cls"
                      :colspan="celda.colspan || undefined"
                    >
                      <span v-if="celda.zero" class="xt-zero">0%</span>
                      <span v-else>{{ celda.val }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </template>
      </div>

      <!-- ── 3. Fechas: no aplica para plantillas ── -->
      <v-card v-if="promo.is_template" outlined class="mb-5 mt-5" style="border-color:#CE93D8;">
        <div class="section-card-title">
          <div class="d-flex align-center">
            <div class="section-num section-num--purple">3</div>
            <v-icon color="deep-purple" class="ml-3 mr-2">mdi-calendar-range</v-icon>
            <span class="subtitle-1 font-weight-bold">Fechas de Disponibilidad</span>
          </div>
        </div>
        <v-divider />
        <v-card-text class="pa-5 text-center">
          <v-icon size="40" color="deep-purple lighten-3">mdi-calendar-blank-outline</v-icon>
          <div class="subtitle-2 deep-purple--text text--lighten-1 mt-2">Las plantillas no tienen fechas configuradas</div>
          <div class="caption grey--text mt-1">
            Al crear una promoción desde esta plantilla podrás elegir las fechas de disponibilidad.
          </div>
        </v-card-text>
      </v-card>

      <!-- ── 3. Fechas de Disponibilidad ── -->
      <v-card v-else outlined class="mb-5 mt-5">
        <div class="section-card-title">
          <div class="d-flex align-center">
            <div class="section-num">3</div>
            <v-icon color="blue darken-4" class="ml-3 mr-2">mdi-calendar-range</v-icon>
            <span class="subtitle-1 font-weight-bold">Fechas de Disponibilidad</span>
            <v-chip x-small color="green darken-1" dark class="ml-3">
              {{ totalDias }} día{{ totalDias !== 1 ? 's' : '' }}
            </v-chip>
          </div>
        </div>
        <v-divider />
        <v-card-text class="pa-5">
          <v-row>
            <!-- Calendario visual -->
            <v-col cols="12" md="6">
              <div class="d-flex align-center justify-space-between mb-4">
                <v-btn icon small @click="prevMonthCal"><v-icon>mdi-chevron-left</v-icon></v-btn>
                <span class="subtitle-2 font-weight-bold">{{ calMesLabel }}</span>
                <v-btn icon small @click="nextMonthCal"><v-icon>mdi-chevron-right</v-icon></v-btn>
              </div>
              <div class="cal-grid">
                <div v-for="d in ['Lu','Ma','Mi','Ju','Vi','Sá','Do']" :key="d" class="cal-head">{{ d }}</div>
                <div v-for="n in calPrimerDia" :key="'e'+n" class="cal-cell cal-empty"></div>
                <div
                  v-for="dia in calDiasDelMes"
                  :key="dia"
                  class="cal-cell"
                  :class="getDayClass(dia)"
                >
                  <span>{{ dia }}</span>
                </div>
              </div>
            </v-col>
            <!-- Lista de rangos -->
            <v-col cols="12" md="6">
              <div class="d-flex align-center mb-3">
                <v-icon small color="grey darken-1" class="mr-2">mdi-calendar-check-outline</v-icon>
                <span class="caption font-weight-bold grey--text text--darken-2">Rangos configurados</span>
                <v-badge
                  v-if="promo.fechas && promo.fechas.length > 0"
                  :content="String(promo.fechas.length)"
                  color="green darken-1"
                  inline class="ml-2"
                />
              </div>
              <div v-if="!promo.fechas || promo.fechas.length === 0" class="fechas-empty">
                <v-icon color="grey lighten-2" size="36">mdi-calendar-blank-outline</v-icon>
                <div class="caption grey--text mt-2">Sin fechas registradas</div>
              </div>
              <div
                v-for="(f, i) in promo.fechas"
                :key="i"
                class="fecha-item"
                :class="f.fecha_inicio === f.fecha_fin ? 'fecha-item--dia' : 'fecha-item--rango'"
              >
                <v-icon small :color="f.fecha_inicio === f.fecha_fin ? 'teal darken-1' : 'green darken-2'" class="mr-2">
                  {{ f.fecha_inicio === f.fecha_fin ? 'mdi-calendar' : 'mdi-calendar-range' }}
                </v-icon>
                <div class="fecha-item-body" style="flex:1;">
                  <div class="caption font-weight-bold">{{ formatFecha(f.fecha_inicio) }}</div>
                  <div v-if="f.fecha_inicio !== f.fecha_fin" class="caption grey--text" style="font-size:10px;">
                    hasta {{ formatFecha(f.fecha_fin) }} · {{ diasEnRango(f) }} días
                  </div>
                  <div v-else class="caption grey--text" style="font-size:10px;">Día único</div>
                  <div class="d-flex flex-wrap mt-1" style="gap:6px; font-size:10px;">
                    <span v-if="f.creado_por" class="grey--text text--darken-1 d-flex align-center">
                      <v-icon style="font-size:10px;" class="mr-1" color="grey darken-1">mdi-account-plus-outline</v-icon>
                      {{ f.creado_por }}<span v-if="f.created_at" class="ml-1">({{ f.created_at }})</span>
                    </span>
                    <span v-if="f.actualizado_por" class="blue--text text--darken-1 d-flex align-center">
                      <v-icon style="font-size:10px;" class="mr-1" color="blue darken-1">mdi-account-edit-outline</v-icon>
                      {{ f.actualizado_por }}<span v-if="f.updated_at" class="ml-1">({{ f.updated_at }})</span>
                    </span>
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

    </div>

    <!-- Dialog confirmar aprobación -->
    <v-dialog v-model="dialogAprobar" width="420" persistent>
      <v-card>
        <v-card-title class="subtitle-1 font-weight-bold py-4 px-5">
          <v-icon color="green darken-1" class="mr-2">mdi-check-circle-outline</v-icon>
          Aprobar Plan de Promoción
        </v-card-title>
        <v-card-text class="px-5 pb-2">
          ¿Estás seguro de que deseas <strong>aprobar</strong> este plan de promoción?<br>
          Una vez aprobado, el estado cambiará a <strong>Aprobada</strong>.
        </v-card-text>
        <v-card-actions class="px-5 py-3">
          <v-spacer />
          <v-btn text small @click="dialogAprobar = false">Cancelar</v-btn>
          <v-btn color="green darken-1" dark depressed small :loading="aprobando" @click="aprobarPlan">
            <v-icon left x-small>mdi-check</v-icon> Aprobar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import xaxios from '@/xaxios';
import gql from 'graphql-tag';

export default {
  name: 'PromoDetailV3',
  data() {
    return {
      loading: false,
      error: false,
      promo: null,
      dialogAprobar: false,
      aprobando: false,
      unidades: [],
      conceptoMap: {},
      categoriaMap: {},
      areaMap: {},
      calDate: new Date(),
      vistaTabla: false,
      tiposListaPrecios: [],
      estadosProgramas: [],
      conceptosHeaders: [
        { text: 'Concepto',      value: 'nombre',          sortable: false },
        { text: 'Precio Base',   value: 'precio_base_fmt', sortable: false, align: 'right', width: 120 },
        { text: 'Tipo desc.',    value: 'is_porcentaje',   sortable: false, align: 'center', width: 90 },
        { text: 'Descuento',     value: 'valor_descuento', sortable: false, align: 'center', width: 110 },
        { text: 'Nº Cuotas',    value: 'cuotas',          sortable: false, align: 'center', width: 80 },
        { text: 'Precio Final',  value: 'precio_final',    sortable: false, align: 'right',  width: 120 },
        { text: 'Valor Cuota',   value: 'valor_cuota',     sortable: false, align: 'right',  width: 120 },
      ],
    };
  },
  computed: {
    unidadNombre() {
      if (!this.promo || !this.promo.unidad_negocio_id) return null;
      const u = this.unidades.find(x => x.id === this.promo.unidad_negocio_id);
      return u ? u.nombre : `Unidad #${this.promo.unidad_negocio_id}`;
    },
    // ── Calendario ─────────────────────────────────────────
    calMes() {
      return { y: this.calDate.getFullYear(), m: this.calDate.getMonth() };
    },
    calMesLabel() {
      const meses = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio',
        'Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
      return `${meses[this.calMes.m]} ${this.calMes.y}`;
    },
    calDiasDelMes() {
      const { y, m } = this.calMes;
      return Array.from({ length: new Date(y, m + 1, 0).getDate() }, (_, i) => i + 1);
    },
    calPrimerDia() {
      const { y, m } = this.calMes;
      return (new Date(y, m, 1).getDay() + 6) % 7;
    },
    totalDias() {
      if (!this.promo || !this.promo.fechas) return 0;
      return this.promo.fechas.reduce((acc, f) => acc + this.diasEnRango(f), 0);
    },
    tablaData() {
      if (!this.promo || !this.promo.reglas || this.promo.reglas.length === 0) return null;

      // Recopilar todos los nombres de conceptos únicos (orden de aparición)
      const conceptosSet = new Set();
      this.promo.reglas.forEach(r => {
        (r._conceptos || []).forEach(c => conceptosSet.add(c.nombre));
      });
      const conceptos = [...conceptosSet];

      // Agrupar reglas por categoría
      const grupoMap = new Map();
      this.promo.reglas.forEach((r, ri) => {
        const key = r._categoria || '__sin_cat__';
        if (!grupoMap.has(key)) {
          grupoMap.set(key, { nombre: r._categoria || 'Sin categoría', filas: [] });
        }
        const conceptoMap = {};
        (r._conceptos || []).forEach(c => { conceptoMap[c.nombre] = c; });
        grupoMap.get(key).filas.push({ ri, regla: r, conceptoMap });
      });

      return { conceptos, grupos: [...grupoMap.values()] };
    },
    tablaColumnasSub() {
      if (!this.tablaData) return [];
      const cols = [];
      this.tablaData.conceptos.forEach(c => {
        cols.push({ key: c+'_b', label: 'BASE',     cls: '' });
        cols.push({ key: c+'_d', label: 'DESC',     cls: 'xt-th-sub--desc' });
        cols.push({ key: c+'_n', label: 'Nº CUOTA', cls: '' });
        cols.push({ key: c+'_f', label: 'FINAL',    cls: 'xt-th-sub--final' });
        cols.push({ key: c+'_v', label: 'V. CUOTA', cls: 'xt-th-sub--cuota' });
      });
      cols.push({ key: '__sb', label: 'TOTAL BASE',  cls: 'xt-th-sub--s' });
      cols.push({ key: '__sf', label: 'TOTAL FINAL', cls: 'xt-th-sub--s xt-th-sub--final' });
      cols.push({ key: '__sa', label: 'AHORRO',      cls: 'xt-th-sub--s xt-th-sub--desc' });
      cols.push({ key: '__sc', label: '1RA CUOTA',   cls: 'xt-th-sub--s xt-th-sub--cuota' });
      return cols;
    },
  },
  methods: {
    // ── Estilo / Labels ──────────────────────────────────────
    estadoColor(estado) {
      const map = {
        0: 'red darken-1', 1: 'green darken-1',
        Activo: 'green darken-1', Inactivo: 'grey', Borrador: 'orange darken-1', Pendiente: 'blue darken-2',
      };
      return map[estado] !== undefined ? map[estado] : 'grey';
    },
    async aprobarPlan() {
      this.aprobando = true;
      const us = this.$store.state.DATA_USER;
      const aprobado_por = us ? `${us.nombres || ''} ${us.pri_apellido || ''} ${us.seg_apellido || ''}`.trim() : '';
      try {
        await xaxios.request({
          method: 'patch',
          url: `${process.env.VUE_APP_API_REST}/api/v1/esam/promociones`,
          data: { id: this.promo.id, aprobado_por },
        });
        this.dialogAprobar = false;
        this.promo = { ...this.promo, estado: 1, aprobado_por };
        this.$notify({ group: 'auth', type: 'success', title: 'Plan aprobado', text: 'El plan de promoción fue aprobado correctamente.' });
      } catch (e) {
        const detalle = e?.response?.data?.message || e?.response?.data?.error || e?.message || '';
        this.$notify({ group: 'auth', type: 'error', title: 'Error al aprobar', text: detalle || 'No se pudo aprobar el plan. Intenta nuevamente.' });
      } finally {
        this.aprobando = false;
      }
    },
    labelModalidad(val) {
      const map = { teorico: 'Teórico', practico: 'Práctico', teorico_practico: 'Teórico-Práctico' };
      return map[val] || val;
    },
    labelTipoPrecio(val) {
      if (!val) return val;
      const found = this.tiposListaPrecios.find(t => t.id == val);
      return found ? found.nombre : val;
    },
    labelEstadoPrograma(id) {
      if (!id) return null;
      const found = this.estadosProgramas.find(e => e.id === id);
      return found ? found.nombre : `Estado #${id}`;
    },
    estadoColorPrograma(id) {
      const found = this.estadosProgramas.find(e => e.id === id);
      if (!found) return 'grey';
      const code = found.codigo || '';
      if (code.startsWith('EN_CURSO'))                        return 'green darken-2';
      if (code.startsWith('EN_ARRASTRE_DESCARTADO'))          return 'grey darken-1';
      if (code.startsWith('EN_ARRASTRE_INICIADO_SIN_PUNTO'))  return 'deep-orange darken-2';
      if (code.startsWith('EN_ARRASTRE'))                     return 'orange darken-2';
      if (code.startsWith('TITULADO'))                        return 'blue darken-2';
      if (code.startsWith('EN_PROCESO_TITULACION'))           return 'blue';
      if (code.startsWith('SE_QUEDA'))                        return 'cyan darken-2';
      if (code.startsWith('NO_') || code.startsWith('PROBLEMAS')) return 'red darken-1';
      return 'grey';
    },
    async loadEstadosProgramas() {
      try {
        const res = await xaxios.request({
          method: 'get',
          url: `${process.env.VUE_APP_API_REST}/api/v1/programa/estados`,
        });
        const raw = res.data;
        this.estadosProgramas = raw.data || raw.datos || raw || [];
      } catch (e) {
        window.console.error('Error cargando estados de programa', e);
      }
    },
    async loadTiposListaPrecios() {
      try {
        const { data } = await xaxios.request({
          method: 'get',
          url: `${process.env.VUE_APP_URL_API_PAGOS}tipos-lista-precios`,
          params: { universidadId: 9 },
        });
        this.tiposListaPrecios = data.datos || [];
      } catch (e) {
        window.console.error('Error cargando tipos de lista de precios', e);
      }
    },

    // ── Calendario ──────────────────────────────────────────
    prevMonthCal() {
      const { y, m } = this.calMes;
      this.calDate = new Date(y, m - 1, 1);
    },
    nextMonthCal() {
      const { y, m } = this.calMes;
      this.calDate = new Date(y, m + 1, 1);
    },
    parseDate(str) {
      const [y, m, d] = str.split('-').map(Number);
      return new Date(y, m - 1, d);
    },
    formatFecha(str) {
      const d = this.parseDate(str);
      const meses = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
      return `${d.getDate()} ${meses[d.getMonth()]} ${d.getFullYear()}`;
    },
    diasEnRango(f) {
      const ini = this.parseDate(f.fecha_inicio);
      const fin = this.parseDate(f.fecha_fin);
      return Math.round((fin - ini) / 86400000) + 1;
    },
    getDayClass(dia) {
      if (!this.promo || !this.promo.fechas) return [];
      const { y, m } = this.calMes;
      const d = new Date(y, m, dia);
      for (const f of this.promo.fechas) {
        const ini = this.parseDate(f.fecha_inicio);
        const fin = this.parseDate(f.fecha_fin);
        if (d.getTime() === ini.getTime() && d.getTime() === fin.getTime()) return ['cal-day--selected'];
        if (d.getTime() === ini.getTime()) return ['cal-day--range-start'];
        if (d.getTime() === fin.getTime()) return ['cal-day--range-end'];
        if (d > ini && d < fin) return ['cal-day--in-range'];
      }
      return [];
    },

    // ── Cálculos ────────────────────────────────────────────
    pFinal(con) {
      const base = parseFloat(con.precio_base) || 0;
      const desc = parseFloat(con.valor_descuento) || 0;
      if (con.is_porcentaje) return base * (1 - desc / 100);
      return Math.max(base - desc, 0);
    },
    vCuota(con) { return this.pFinal(con) / Math.max(con.cuotas || 1, 1); },
    tBase(regla) { return (regla._conceptos || []).reduce((a, c) => a + (parseFloat(c.precio_base) || 0), 0); },
    tFinal(regla) { return (regla._conceptos || []).reduce((a, c) => a + this.pFinal(c), 0); },
    tAhorro(regla) { return this.tBase(regla) - this.tFinal(regla); },
    tPrimeraCuota(regla) { return (regla._conceptos || []).reduce((a, c) => a + this.vCuota(c), 0); },

    getCeldasFila(fila) {
      const celdas = [];
      this.tablaData.conceptos.forEach(cNombre => {
        const c = fila.conceptoMap[cNombre];
        if (c) {
          const desc = parseFloat(c.valor_descuento) || 0;
          celdas.push({ key: cNombre+'_b', cls: 'xt-td xt-td-num',   val: parseFloat(c.precio_base).toFixed(2) });
          celdas.push({ key: cNombre+'_d', cls: 'xt-td xt-td-desc',  val: desc > 0 ? (desc + (c.is_porcentaje ? '%' : ' Bs')) : null, zero: desc <= 0 });
          celdas.push({ key: cNombre+'_n', cls: 'xt-td xt-td-num',   val: c.cuotas || 1 });
          celdas.push({ key: cNombre+'_f', cls: 'xt-td xt-td-final', val: this.pFinal(c).toFixed(2) });
          celdas.push({ key: cNombre+'_v', cls: 'xt-td xt-td-cuota', val: this.vCuota(c).toFixed(2) });
        } else {
          celdas.push({ key: cNombre+'_e', cls: 'xt-td xt-td-empty', val: '—', colspan: 5 });
        }
      });
      celdas.push({ key: '__sb', cls: 'xt-td xt-td-num xt-td-s',                   val: this.tBase(fila.regla).toFixed(2) });
      celdas.push({ key: '__sf', cls: 'xt-td xt-td-final xt-td-s xt-td-s--final',  val: this.tFinal(fila.regla).toFixed(2) });
      celdas.push({ key: '__sa', cls: 'xt-td xt-td-desc xt-td-s xt-td-s--desc',    val: this.tAhorro(fila.regla).toFixed(2) });
      celdas.push({ key: '__sc', cls: 'xt-td xt-td-cuota xt-td-s xt-td-s--cuota',  val: this.tPrimeraCuota(fila.regla).toFixed(2) });
      return celdas;
    },
    // ── Usar como plantilla ──────────────────────────────────
    usarComoPlantilla() {
      localStorage.setItem('promo_v3_template_pending', JSON.stringify({ source: 'api', id: this.promo.id }));
      this.$router.push('/promociones-v3/nueva');
    },

    // ── Cargar datos ─────────────────────────────────────────
    async loadUnidades() {
      try {
        const res = await this.$apollo.query({
          query: gql`query { NegociosAll { id nombre } }`,
        });
        this.unidades = res.data.NegociosAll || [];
      } catch { /* ignore */ }
    },
    async loadConceptoMap(unidadId) {
      try {
        const res = await xaxios.request({
          method: 'post',
          url: `${process.env.VUE_APP_API_REST}/api/pagos/conceptos/buscar`,
          data: { es_nacional: false, unidad_negocio_id: unidadId },
        });
        (res.data || []).forEach(c => { this.conceptoMap[c.id] = c.nombre; });
      } catch { /* ignore */ }
    },
    async loadCategoriaMap(unidadId) {
      try {
        const res = await this.$apollo.query({
          query: gql`query($id: Int!) { getCategorias(unidad_negocio_id: $id) { id nombre } }`,
          variables: { id: unidadId },
          fetchPolicy: 'network-only',
        });
        (res.data.getCategorias || []).forEach(c => { this.categoriaMap[c.id] = c.nombre; });
      } catch { /* ignore */ }
    },
    async loadAreaMap(unidadId) {
      try {
        const res = await this.$apollo.query({
          query: gql`query($id: Int!) { getAreas(unidad_negocio_id: $id) { id nombre } }`,
          variables: { id: unidadId },
          fetchPolicy: 'network-only',
        });
        (res.data.getAreas || []).forEach(a => { this.areaMap[a.id] = a.nombre; });
      } catch { /* ignore */ }
    },
    async loadPromo() {
      this.loading = true;
      this.error = false;
      try {
        const id = this.$route.params.id;
        const res = await xaxios.request({
          method: 'get',
          url: `${process.env.VUE_APP_API_REST}/api/v1/esam/promociones/v3/${id}`,
        });
        const data = res.data.data || res.data;

        // Cargar datos de apoyo en paralelo
        await Promise.all([
          this.loadUnidades(),
          data.unidad_negocio_id ? this.loadConceptoMap(data.unidad_negocio_id) : Promise.resolve(),
          data.unidad_negocio_id ? this.loadCategoriaMap(data.unidad_negocio_id) : Promise.resolve(),
          data.unidad_negocio_id ? this.loadAreaMap(data.unidad_negocio_id) : Promise.resolve(),
        ]);

        // Enriquecer reglas con nombres
        data.reglas = (data.reglas || []).map(r => ({
          ...r,
          _categoria: this.categoriaMap[r.categoria_id] || null,
          _area:      this.areaMap[r.area_id]           || null,
          _conceptos: (r.conceptos || []).map(c => ({
            ...c,
            nombre:          this.conceptoMap[c.concepto_pago_id] || `Concepto #${c.concepto_pago_id}`,
            precio_base_fmt: `Bs ${parseFloat(c.precio_base).toFixed(2)}`,
          })),
        }));

        this.promo = data;

        // Posicionar calendario en el primer mes con fechas
        if (data.fechas && data.fechas.length > 0) {
          const primera = this.parseDate(data.fechas[0].fecha_inicio);
          this.calDate = new Date(primera.getFullYear(), primera.getMonth(), 1);
        }
      } catch {
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.loadPromo();
    this.loadTiposListaPrecios();
    this.loadEstadosProgramas();
  },
};
</script>

<style scoped>
/* ── Página ─────────────────────────────────── */
.pd3-page {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: #f5f5f5;
}

/* ── Top bar ────────────────────────────────── */
.pd3-topbar {
  display: flex;
  align-items: center;
  padding: 8px 20px;
  background: #fff;
  border-bottom: 1px solid #e0e0e0;
  position: sticky;
  top: 0;
  z-index: 10;
  gap: 6px;
}
.pd3-topbar--template {
  background: #F3E5F5;
  border-bottom-color: #CE93D8;
}

/* ── Banner plantilla ───────────────────────── */
.template-banner {
  display: flex;
  align-items: center;
  background: #EDE7F6;
  border-bottom: 1px solid #CE93D8;
  padding: 8px 24px;
  font-size: 13px;
  color: #4A148C;
}

/* ── Cuerpo ─────────────────────────────────── */
.pd3-body {
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 24px 32px 60px;
}

/* ── Detail blocks ──────────────────────────── */
.detail-block { }
.detail-label {
  font-size: 11px;
  font-weight: 700;
  color: #9e9e9e;
  letter-spacing: 0.4px;
  margin-bottom: 4px;
}
.detail-value {
  font-size: 14px;
  color: #212121;
  font-weight: 500;
}

/* ── Section title ──────────────────────────── */
.section-card-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
}
.section-num {
  width: 28px;
  height: 28px;
  background: #1565c0;
  color: white;
  border-radius: 50%;
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.section-num--purple { background: #7B1FA2; }

/* ── Calendario ─────────────────────────────── */
.cal-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 3px;
}
.cal-head {
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  color: #9e9e9e;
  padding: 4px 0 8px;
}
.cal-cell {
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 500;
  color: #424242;
  border-radius: 50%;
  user-select: none;
}
.cal-empty { color: #ccc; }
.cal-day--selected,
.cal-day--range-start,
.cal-day--range-end    { background: #4CAF50 !important; color: white !important; font-weight: 700; }
.cal-day--in-range     { background: #E8F5E9 !important; color: #1B5E20 !important; border-radius: 0; }

/* ── Fechas panel ───────────────────────────── */
.fechas-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;
  border: 1px dashed #e0e0e0;
  border-radius: 8px;
}
.fecha-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid;
  margin-bottom: 6px;
}
.fecha-item--rango { background: #F1F8E9; border-color: #C8E6C9; }
.fecha-item--dia   { background: #E0F2F1; border-color: #B2DFDB; }
.fecha-item-body   { flex: 1; min-width: 0; }

/* ── Regla card ─────────────────────────────── */
.regla-card { border-left: 4px solid #1565c0 !important; }
.regla-hdr {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  border-bottom: 1px solid #f0f0f0;
  gap: 8px;
  flex-wrap: wrap;
}

/* ── Conceptos table ────────────────────────── */
.conceptos-tbl {
  border: 1px solid #eeeeee;
  border-radius: 4px;
}

/* ── Totales ────────────────────────────────── */
.regla-totals {
  display: flex;
  min-height: 68px;
}
.rtot-cell {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 10px 16px;
}
.rtot-cell--green { background: #F1F8E9; }
.rtot-cell--blue  { background: #E3F2FD; }
.rtot-cell--dark  { background: #212121; border-radius: 0 0 3px 0; }
.rtot-label       { font-size: 10px; font-weight: 700; color: #888; letter-spacing: 0.3px; display:flex; align-items:center; }
.rtot-label--green{ color: #2e7d32; }
.rtot-label--blue { color: #1565c0; }
.rtot-label--light{ color: rgba(255,255,255,.7); }
.rtot-value       { font-size: 14px; font-weight: 700; color: #333; margin-top: 3px; }
.rtot-value--lg   { font-size: 18px; }
.rtot-value--green{ color: #2e7d32; }
.rtot-value--blue { color: #1565c0; }
.rtot-value--white{ color: #fff; }

/* ── Vista Tabla ────────────────────────────── */
.xtabla-wrap { }

.xtabla-cat-hdr {
  display: flex;
  align-items: center;
  background: #1565c0;
  color: #fff;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.6px;
  padding: 7px 14px;
  border-radius: 4px 4px 0 0;
}

.xtabla-scroll {
  overflow-x: auto;
  border: 1px solid #cfd8dc;
  border-top: none;
  border-radius: 0 0 4px 4px;
}

.xtabla {
  border-collapse: collapse;
  width: max-content;
  min-width: 100%;
  font-size: 12px;
  background: #fff;
}

/* TH base */
.xt-th {
  padding: 6px 10px;
  border: 1px solid #b0bec5;
  text-align: center;
  font-weight: 700;
  font-size: 10px;
  letter-spacing: 0.4px;
  white-space: nowrap;
}

/* TH sticky izquierda */
.xt-th-area {
  background: #263238;
  color: #fff;
  position: sticky;
  left: 0;
  z-index: 3;
  min-width: 110px;
  text-align: left;
}

/* TH columna meta (modalidad/plan) */
.xt-th-meta {
  background: #37474f;
  color: #eceff1;
  position: sticky;
  left: 110px;
  z-index: 3;
  min-width: 90px;
  font-size: 9px;
  line-height: 1.3;
}

/* Cabeceras de grupo de conceptos */
.xt-th-group {
  background: #c8a200;
  color: #fff;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.5px;
  border-bottom: none;
}

.xt-th-group--summary {
  background: #1565c0;
}

/* Sub-columnas */
.xt-th-sub {
  background: #fff8e1;
  color: #5d4037;
  font-size: 9px;
  padding: 4px 8px;
  border-top: 2px solid #c8a200;
}
.xt-th-sub--desc  { background: #fff3e0; color: #e65100; border-top-color: #c8a200; }
.xt-th-sub--final { background: #e8f5e9; color: #1b5e20; border-top-color: #c8a200; }
.xt-th-sub--cuota { background: #e3f2fd; color: #0d47a1; border-top-color: #c8a200; }

/* Sub-columnas de resumen */
.xt-th-sub--s {
  background: #e8eaf6;
  color: #1a237e;
  border-top: 2px solid #1565c0;
}
.xt-th-sub--s.xt-th-sub--final { background: #e8f5e9; color: #1b5e20; border-top-color: #1565c0; }
.xt-th-sub--s.xt-th-sub--desc  { background: #fff3e0; color: #e65100; border-top-color: #1565c0; }
.xt-th-sub--s.xt-th-sub--cuota { background: #e3f2fd; color: #0d47a1; border-top-color: #1565c0; }

/* Celdas de datos */
.xt-td {
  padding: 5px 10px;
  border: 1px solid #dde2e6;
  white-space: nowrap;
  vertical-align: middle;
}

/* Área - sticky */
.xt-td-area {
  position: sticky;
  left: 0;
  background: #f5f5f5;
  font-weight: 700;
  font-size: 12px;
  color: #212121;
  min-width: 110px;
  z-index: 2;
  border-right: 2px solid #90a4ae;
}

/* Meta - sticky */
.xt-td-meta {
  position: sticky;
  left: 110px;
  background: #fafafa;
  z-index: 2;
  min-width: 90px;
  border-right: 2px solid #90a4ae;
}

.xt-td-num  { text-align: right; color: #424242; }
.xt-td-desc { text-align: center; color: #e65100; font-weight: 600; }
.xt-td-final{ text-align: right; color: #2e7d32; font-weight: 700; }
.xt-td-cuota{ text-align: right; color: #1565c0; font-weight: 700; }
.xt-td-empty{ text-align: center; color: #bdbdbd; }

/* Celdas de resumen */
.xt-td-s        { border-left: 2px solid #90a4ae; background: #f5f5f5; }
.xt-td-s--final { background: #f1f8e9; color: #1b5e20 !important; }
.xt-td-s--desc  { background: #fff8f0; color: #e65100 !important; }
.xt-td-s--cuota { background: #e8f4fd; color: #0d47a1 !important; }

/* Filas alternadas */
.xt-tr-even .xt-td-area,
.xt-tr-even .xt-td-meta { background: #f5f5f5; }
.xt-tr-odd  .xt-td-area,
.xt-tr-odd  .xt-td-meta { background: #eeeeee; }
.xt-tr-even { background: #fff; }
.xt-tr-odd  { background: #f9f9f9; }
.xt-tr-even:hover,
.xt-tr-odd:hover { background: #e8f4fb; }
.xt-tr-even:hover .xt-td-area,
.xt-tr-odd:hover  .xt-td-area,
.xt-tr-even:hover .xt-td-meta,
.xt-tr-odd:hover  .xt-td-meta { background: #d1ecf9; }

/* Badges dentro de la celda meta */
.xt-badge {
  display: inline-block;
  font-size: 9px;
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 3px;
  margin-bottom: 2px;
  white-space: nowrap;
}
.xt-badge--purple { background: #ede7f6; color: #4a148c; }
.xt-badge--orange { background: #fff3e0; color: #e65100; }
.xt-badge--brown  { background: #efebe9; color: #4e342e; }

/* Cero descuento */
.xt-zero { color: #bdbdbd; font-weight: 400; }
</style>
